import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalPage, LegalSection } from "@/components/wallet/LegalPage";

export const SUPPORT_EMAIL = "support@cobaltwalletxrpl.app";

export const Route = createFileRoute("/support")({
  staticData: { sitemap: true },
  head: () => ({
    meta: [
      { title: "Support & Help — Cobalt Wallet" },
      {
        name: "description",
        content:
          "Get help with Cobalt Wallet: recovery phrases, passcode and biometric unlock, trustlines, buying and selling XRP, fees, and deleting your wallet data.",
      },
      { property: "og:title", content: "Support & Help — Cobalt Wallet" },
      {
        property: "og:description",
        content:
          "Help centre for Cobalt Wallet, the non-custodial XRP Ledger wallet. Contact, FAQs and data deletion.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://cobaltwalletxrpl.com/support" }],
  }),
  component: SupportPage,
});

function SupportPage() {
  return (
    <LegalPage
      eyebrow="Support"
      title="Support & Help"
      updated="4 September 2026"
      intro="Cobalt Wallet is self-custody software. We cannot access your wallet, reverse a transaction or recover a lost recovery phrase — but we can help you understand the app."
    >
      <LegalSection heading="Contact us">
        <p>
          Email{" "}
          <a
            className="text-accent underline"
            href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("Cobalt Wallet support request")}&body=${encodeURIComponent("Describe the issue (do NOT include your recovery phrase, 25th word or passcode):\n\n\nApp version / platform:\n")}`}
          >
            {SUPPORT_EMAIL}
          </a>{" "}
          and we aim to reply within two business days. This is the same support address listed on
          our App Store and Google Play listings.
        </p>

        <p>
          Never send your recovery phrase, 25th word, passcode or private key to anyone — including
          anyone claiming to be Cobalt support. We will never ask for them.
        </p>
      </LegalSection>

      <LegalSection heading="Recovery phrase and access">
        <p>
          Your 12 or 24 word phrase (plus your optional 25th word) is the only way to restore this
          wallet. It is generated on your device and never transmitted. Write it down offline and
          store it somewhere only you can reach.
        </p>
        <p>
          If you forget your six-digit passcode, reinstall or reset the wallet and import your
          recovery phrase again. Without the phrase, the funds cannot be recovered by anyone.
        </p>
      </LegalSection>

      <LegalSection heading="Buying and selling XRP">
        <p>
          Buy and Sell compare live rates across third-party providers. Those purchases and payouts
          are performed by the provider you choose under their own terms, KYC and refund rules.
          Cobalt Wallet is not the seller of record and does not take custody of your funds or your
          cash.
        </p>
        <p>
          When sending XRP to an exchange deposit address, always include the destination tag the
          exchange gives you. Deposits sent without a tag are frequently unrecoverable.
        </p>
      </LegalSection>

      <LegalSection heading="Trustlines and tokens">
        <p>
          Adding a trustline reserves a small amount of XRP on the ledger. Removing a trustline
          returns that reserve, and requires the token balance to be zero. Any issuer can create a
          token with any name — verify the issuer address before you trust it.
        </p>
      </LegalSection>

      <LegalSection heading="Fees">
        <p>
          Every transaction pays the XRP Ledger network fee. Cobalt Wallet also charges a service fee
          on some actions, always displayed before you confirm and only collected after the ledger
          reports your transaction succeeded. Third-party buy/sell providers charge their own spread
          and fees.
        </p>
      </LegalSection>

      <LegalSection heading="Delete your data">
        <p>
          Cobalt Wallet stores no accounts and holds no personal data on any server, so there is
          nothing for us to delete on your behalf. All wallet data lives on your device.
        </p>
        <p>
          To erase it: open Settings and delete the wallet, or uninstall the app (mobile) / clear
          site data for this domain (browser). This permanently removes the encrypted vault, address
          book and preferences from the device. Make sure your recovery phrase is backed up first —
          this cannot be undone.
        </p>
        <p>
          For any deletion question, email{" "}
          <a className="text-accent underline" href={`mailto:${SUPPORT_EMAIL}`}>
            {SUPPORT_EMAIL}
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection heading="Report a security issue">
        <p>
          Please disclose vulnerabilities privately to{" "}
          <a className="text-accent underline" href={`mailto:${SUPPORT_EMAIL}`}>
            {SUPPORT_EMAIL}
          </a>{" "}
          before publishing them, and allow reasonable time for a fix.
        </p>
        <p>
          Our full custody and key-storage model is documented on the{" "}
          <Link className="text-accent underline" to="/security">
            security model
          </Link>{" "}
          page.
        </p>
      </LegalSection>
    </LegalPage>
  );
}
